/**
 * Attendance confirmation — whether a nomination's guest is confirmed to attend, and who may
 * change that.
 *
 * Mirrors AWSECEventHub NominationService.updateAttendance: only the nomination's creator, and
 * only once it is APPROVED. The creator comparison is exact, as the service's is
 * (`actor.equals(createdBy)`), so the control is never offered to someone it would 403 — and the
 * login is the one Event Hub acts as, so an admin acting as the creator gets it, as the service
 * allows.
 *
 * Here rather than in the components because the coverage config measures only `.ts`.
 */

import type { Nomination } from '../client/EventCalendarClient';
import { formatDateTime } from './eventDates';

/** Confirmed to attend. Absent (never set) reads as not confirmed, as the service defines it. */
export function isAttendanceConfirmed(nomination: Nomination): boolean {
  return nomination.attendanceConfirmation === 'CONFIRMED';
}

/** Whether `login` may confirm or withdraw this nomination's attendance. */
export function canConfirmAttendance(nomination: Nomination, login: string | undefined): boolean {
  return (
    nomination.status === 'APPROVED' &&
    !!login &&
    !!nomination.createdBy &&
    nomination.createdBy === login
  );
}

/**
 * Who confirmed the guest's attendance and when, for the 确认出席 cell's tooltip — the audit
 * question ("who ticked this?") without a column of its own. Undefined unless confirmed, and
 * either half is left out when the service did not record it.
 */
export function attendanceConfirmedNote(nomination: Nomination): string | undefined {
  if (!isAttendanceConfirmed(nomination)) return undefined;
  const by = nomination.attendanceConfirmedBy?.trim();
  const at = nomination.attendanceConfirmedAt;
  if (!by && !at) return undefined;
  return ['Confirmed', by && `by ${by}`, at && `on ${formatDateTime(at)}`].filter(Boolean).join(' ');
}
